import { FloatingContext } from '@floating-ui/react'
import { PermissionSet, usePermission } from '@root/context'
import React, { useState } from 'react'
import { collectFromPages, useInfiniteTags } from '@root/query'
import { ITag } from '@root/query/tags'
import { Button } from '../../components/Button'
import { FaPen, FaPlus } from 'react-icons/fa'
import TagSelectorDropDown, { SelectableTag, TagSelectorItemAction } from './TagSelectorDropDown'

interface Props {
  context: FloatingContext
  refs: FloatingContext['refs']
  getFloatingProps: (userProps?: React.HTMLProps<HTMLElement>) => Record<string, unknown>
  isOnlySelect: boolean
  onTagSelect?: (tagId: number) => void
  isSelected?: (tag: SelectableTag<number>) => boolean
  onEditTag?: (tag: ITag) => void
  onCreateTag?: () => void
}

export function DocumentTagSelectorDropDown({
  context,
  refs,
  getFloatingProps,
  isOnlySelect,
  onTagSelect,
  isSelected,
  onEditTag,
  onCreateTag
}: Props) {
  const canWriteTags = usePermission(PermissionSet.BOOKKEEPING_EDIT)
  const hideTagManagement = isOnlySelect || !canWriteTags
  const [search, setSearch] = useState('')
  const { data, isLoading, hasNextPage, fetchNextPage } = useInfiniteTags({ search })
  const documentTags = collectFromPages(data)
  const items: SelectableTag<number>[] = documentTags.map(tag => ({
    id: tag.id,
    name: tag.name,
    description: tag.description,
    color: tag.color
  }))

  const editDocumentTag = (tag: SelectableTag<number>) => {
    const documentTag = documentTags.find(item => item.id === tag.id)
    if (documentTag) onEditTag?.(documentTag)
  }

  return (
    <TagSelectorDropDown
      context={context}
      refs={refs}
      getFloatingProps={getFloatingProps}
      items={items}
      isLoading={isLoading}
      onSearchChange={setSearch}
      onScrollBottom={() => {
        if (hasNextPage && !isLoading) {
          fetchNextPage()
        }
      }}
      onTagSelect={onTagSelect}
      isSelected={isSelected}
      action={
        !hideTagManagement ? (
          <Button
            type="button"
            isSecondary={true}
            icon={<FaPlus />}
            onClick={() => onCreateTag?.()}
          ></Button>
        ) : undefined
      }
      onItemAction={!hideTagManagement ? editDocumentTag : undefined}
      renderItemAction={
        !hideTagManagement
          ? (tag, focused) => (
              <TagSelectorItemAction className={focused ? 'focused' : ''}>
                <Button
                  type="button"
                  isSecondary={true}
                  icon={<FaPen />}
                  onClick={e => {
                    e.stopPropagation()
                    editDocumentTag(tag)
                  }}
                ></Button>
              </TagSelectorItemAction>
            )
          : undefined
      }
    />
  )
}

export default DocumentTagSelectorDropDown
